import axios from 'axios'
import { clientConfig } from '../../../client'
import { ISeriesTypeId } from '../../../types'
import { config } from '../../../lib'
import { asset_type_enum } from '../../types'

const apiUrl = `${clientConfig.kaltura_end_point}asset/action/list`

export const fetchAssetByMediaId = async ({
  MEDIAID,
  ks,
}: {
  MEDIAID: string
  ks: string
}) => {
  return await axios.post(apiUrl, {
    filter: {
      objectType: 'KalturaSearchAssetFilter',
      kSql: `(and (or media_id='${MEDIAID}' epg_id='${MEDIAID}'))`,
    },
    pager: {
      objectType: 'KalturaFilterPager',
      pageIndex: 1,
      pageSize: 25,
    },
    format: 1,
    clientTag: config.kalturaClientTag,
    apiVersion: '6.1.0.28839',
    language: 'heb',
    ks: ks,
  })
}
// Catch-up programs are EPG assets, so the OVP provider the player bundle
// ships with has no entryId to resolve. Phoenix hands back the recorded
// window as a plain manifest instead, which the player takes the same way it
// takes the live channel's manifest.
// applehttp on purpose: the mpegdash source comes back as dash_widevine, and
// pulling DRM config in buys nothing here - the HLS manifest plays in the
// bundle as-is.
const fetchCatchupSource = async ({
  MEDIAID,
  ks,
}: {
  MEDIAID: string
  ks: string
}): Promise<string | null> => {
  const response = await axios.post(
    `${clientConfig.kaltura_end_point}asset/action/getPlaybackContext`,
    {
      assetId: MEDIAID,
      assetType: 'epg',
      contextDataParams: {
        objectType: 'KalturaPlaybackContextOptions',
        context: 'CATCHUP',
        streamerType: 'applehttp',
        urlType: 'DIRECT',
      },
      format: 1,
      clientTag: config.kalturaClientTag,
      apiVersion: '6.1.0.28839',
      language: 'heb',
      ks,
    }
  )
  return response.data?.result?.sources?.[0]?.url || null
}

const fetchMainRail = async (id: number | undefined, ks: string) => {
  return (
    await axios.post(apiUrl, {
      filter: { objectType: 'KalturaChannelFilter', idEqual: id, kSql: '' },
      pager: { objectType: 'KalturaFilterPager', pageIndex: 1, pageSize: 25 },
      format: 1,
      clientTag: config.kalturaClientTag,
      apiVersion: '6.1.0.28839',
      language: 'heb',
      ks: ks,
    })
  ).data.result.objects
}

const fetchPosterRegularRail = async (id: number | undefined, ks: string) => {
  return (
    await axios.post(apiUrl, {
      filter: {
        objectType: 'KalturaChannelFilter',
        idEqual: id,
        kSql: "(and (or asset_type = '1259' ) )",
      },
      pager: { objectType: 'KalturaFilterPager', pageIndex: 1, pageSize: 50 },
      format: 1,
      clientTag: config.kalturaClientTag,
      apiVersion: '6.1.0.28839',
      language: 'heb',
      ks,
    })
  ).data
}
const fetchProgramSeasonsEpisodes = async ({
  seriesId,
  season,
  ks,
  page,
  pagesToFetch,
}: {
  seriesId: number
  season: {
    position: number
    type: number
    name: string
  }
  ks: string
  page: number
  pagesToFetch: number
}) => {
  const props = {
    apiVersion: '6.1.0.28839',
    clientTag: config.kalturaClientTag,
    filter: {
      dynamicOrderBy: {
        name: 'EpisodeNumber',
        objectType: 'KalturaDynamicOrderBy',
        orderBy: 'META_ASC',
      },
      objectType: 'KalturaSearchAssetFilter',
      typeIn: season.type,
      orderBy: 'NAME_ASC',
      kSql: `(and SeriesId='${seriesId}' SeasonNumber=${season.position})`,
    },
    format: 1,
    language: 'heb',
    ks: ks,
    responseProfile: {
      objectType: 'KalturaDetachedResponseProfile',
      relatedProfiles: [
        {
          objectType: 'KalturaDetachedResponseProfile',
          name: 'Episodes_In_Season',
          filter: {
            objectType: 'KalturaAggregationCountFilter',
          },
        },
      ],
    },
    pager: {
      pageSize: pagesToFetch,
      pageIndex: page,
    },
  }
  return axios.post(apiUrl, props)
}
const fetchSeasons = async (programId: string | number | false, ks: string) => {
  return axios.post(apiUrl, {
    filter: {
      objectType: 'KalturaSearchAssetFilter',
      typeIn: asset_type_enum.Episodes,
      dynamicOrderBy: {
        name: 'SeasonNumber',
        objectType: 'KalturaDynamicOrderBy',
        orderBy: 'META_ASC',
      },
      // orderBy: 'NAME_ASC',
      kSql: `(and SeriesID = '${programId}' asset_type = '${asset_type_enum.Episodes}')`,
      groupBy: [
        {
          objectType: 'KalturaAssetMetaOrTagGroupBy',
          value: 'SeasonNumber',
        },
      ],
    },
    pager: {
      pageSize: 50,
      pageIndex: 1,
    },
    format: 1,
    clientTag: config.kalturaClientTag,
    apiVersion: '6.1.0.28839',
    language: 'heb',
    ks: ks,
    responseProfile: {
      objectType: 'KalturaDetachedResponseProfile',
      relatedProfiles: [
        {
          objectType: 'KalturaDetachedResponseProfile',
          name: 'Episodes_In_Season',
          filter: {
            objectType: 'KalturaAggregationCountFilter',
          },
        },
      ],
    },
  })
}
const fetchSimilarPrograms = async (
  seriesIdType: ISeriesTypeId | null,
  ks: string
) => {
  return axios.post(apiUrl, {
    filter: {
      objectType: 'KalturaRelatedFilter',
      idEqual: seriesIdType?.id,
      kSql: `(and asset_type='${seriesIdType?.type}')`,
      excludeWatched: false,
    },
    pager: {
      objectType: 'KalturaFilterPager',
      pageIndex: 1,
      pageSize: 25,
    },
    format: 1,
    clientTag: config.kalturaClientTag,
    apiVersion: '6.1.0.28839',
    language: 'heb',
    ks: ks,
  })
}

const FetchAssetData = async (id: string | number | false, ks: string) => {
  return axios.post(apiUrl, {
    filter: {
      objectType: 'KalturaSearchAssetFilter',
      kSql: `(and (or EntryID = '${id}' ExternalID = '${id}' media_id = '${id}' epg_id = '${id}' ) (or asset_type = '${asset_type_enum.Episodes}' asset_type = '1262' asset_type = '${asset_type_enum.Series}' asset_type = '1267' asset_type = '1261' asset_type = '1260' asset_type = '1262' asset_type = '${asset_type_enum.Season}' asset_type = '${asset_type_enum.Series}' asset_type = '${asset_type_enum.Trailer}' asset_type = '${asset_type_enum.Movies}' asset_type = 'epg' asset_type = '${asset_type_enum.Live}' ) )`,
    },
    pager: {
      objectType: 'KalturaFilterPager',
      pageIndex: 1,
      pageSize: 18,
    },
    format: 1,
    clientTag: config.kalturaClientTag,
    apiVersion: '6.1.0.28839',
    language: 'heb',
    ks: ks,
  })
}

const FetchProgramData = async (id: string | number, ks: string) => {
  return axios.post(apiUrl, {
    filter: {
      objectType: 'KalturaSearchAssetListFilter',
      kSql: `(and (or asset_type = 1259 ) (and SeriesID = '${id}' ) )`,
    },
    pager: {
      objectType: 'KalturaFilterPager',
      pageIndex: 1,
      pageSize: 25,
    },
    format: 1,
    clientTag: config.kalturaClientTag,
    apiVersion: '6.1.0.28839',
    language: 'heb',
    ks: ks,
  })
}

export {
  fetchCatchupSource,
  fetchMainRail,
  fetchPosterRegularRail,
  fetchProgramSeasonsEpisodes,
  fetchSeasons,
  fetchSimilarPrograms,
  FetchAssetData,
  FetchProgramData,
}
